import React, { useState } from 'react';
import { Briefcase, Search, Phone, Mail, Star, MapPin, Wrench, Zap, Droplets, Wind, Plus, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Contractor {
  id: string;
  name: string;
  company: string;
  specialty: string;
  rating: number;
  phone: string;
  email: string;
  location: string;
  jobsCompleted: number;
  status: 'active' | 'inactive';
  licenseNo: string;
}

const SPECIALTY_ICONS: Record<string, React.ElementType> = {
  'HVAC': Wind,
  'Plumbing': Droplets,
  'Electrical': Zap,
  'General': Wrench,
  'Roofing': Briefcase,
  'Landscaping': MapPin,
};

const MOCK_CONTRACTORS: Contractor[] = [
  {
    id: 'c1',
    name: 'Marcus Webb',
    company: 'Webb HVAC Solutions',
    specialty: 'HVAC',
    rating: 4.8,
    phone: '(512) 555-0101',
    email: 'marcus@webbhvac.com',
    location: 'Austin, TX',
    jobsCompleted: 37,
    status: 'active',
    licenseNo: 'TX-HVAC-4920',
  },
  {
    id: 'c2',
    name: 'Dana Krueger',
    company: 'Krueger Plumbing Co.',
    specialty: 'Plumbing',
    rating: 4.6,
    phone: '(512) 555-0182',
    email: 'dana@kruegerplumbing.com',
    location: 'Austin, TX',
    jobsCompleted: 54,
    status: 'active',
    licenseNo: 'TX-PLB-7703',
  },
  {
    id: 'c3',
    name: 'Jerome Alcott',
    company: 'Alcott Electric',
    specialty: 'Electrical',
    rating: 4.9,
    phone: '(737) 555-0219',
    email: 'jerome@alcottelectric.com',
    location: 'Round Rock, TX',
    jobsCompleted: 89,
    status: 'active',
    licenseNo: 'TX-ELEC-0182',
  },
  {
    id: 'c4',
    name: 'Rosa Fuentes',
    company: 'Fuentes General Construction',
    specialty: 'General',
    rating: 4.5,
    phone: '(512) 555-0344',
    email: 'rosa@fuentesconstruction.com',
    location: 'Pflugerville, TX',
    jobsCompleted: 22,
    status: 'active',
    licenseNo: 'TX-GC-3312',
  },
  {
    id: 'c5',
    name: 'Tom Brinkley',
    company: 'TexRoof Specialists',
    specialty: 'Roofing',
    rating: 4.3,
    phone: '(512) 555-0455',
    email: 'tom@texroof.com',
    location: 'Cedar Park, TX',
    jobsCompleted: 61,
    status: 'inactive',
    licenseNo: 'TX-RF-8844',
  },
];

const SPECIALTIES = ['All', 'HVAC', 'Plumbing', 'Electrical', 'General', 'Roofing', 'Landscaping'];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={12}
          className={star <= Math.floor(rating) ? 'text-[var(--t-warning)] fill-[var(--t-warning)]' : 'text-[var(--t-border)]'}
        />
      ))}
      <span className="text-xs font-bold text-[var(--t-text)] ml-1">{rating.toFixed(1)}</span>
    </div>
  );
}

export default function Contractors() {
  const [contractors] = useState<Contractor[]>(MOCK_CONTRACTORS);
  const [searchQuery, setSearchQuery] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('All');

  const filtered = contractors.filter(c => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      c.name.toLowerCase().includes(query) ||
      c.company.toLowerCase().includes(query) ||
      c.location.toLowerCase().includes(query) ||
      c.email.toLowerCase().includes(query);
    const matchesSpecialty = specialtyFilter === 'All' || c.specialty === specialtyFilter;
    return matchesSearch && matchesSpecialty;
  });

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between px-8 py-6 bg-[var(--t-surface)] border-b border-[var(--t-border)] shrink-0 gap-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-[var(--t-warning-dim)] flex items-center justify-center">
            <Briefcase className="text-[var(--t-warning)]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--t-text)] m-0">Contractor Directory</h1>
            <p className="text-sm text-[var(--t-text-muted)] mt-1">
              Manage vetted contractors, specialties, and service contacts
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]"
              size={16}
            />
            <input
              type="text"
              placeholder="Search contractors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] w-64"
            />
          </div>

          <button
            onClick={() => toast.success('Invite contractor feature coming soon!')}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors shadow-lg shadow-[var(--t-primary-dim)]"
          >
            <Plus size={16} /> Invite Contractor
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-8 bg-[var(--t-bg)]">
        <div className="max-w-7xl mx-auto space-y-6">

          {/* Specialty Filter Pills */}
          <div className="flex gap-2 flex-wrap">
            {SPECIALTIES.map((sp) => {
              const Icon = SPECIALTY_ICONS[sp] || Briefcase;
              return (
                <button
                  key={sp}
                  onClick={() => setSpecialtyFilter(sp)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all border ${
                    specialtyFilter === sp
                      ? 'bg-[var(--t-primary)] text-white border-[var(--t-primary)] shadow-md'
                      : 'bg-[var(--t-surface)] text-[var(--t-text-muted)] border-[var(--t-border)] hover:border-[var(--t-primary)]/50 hover:text-[var(--t-text)]'
                  }`}
                >
                  {sp !== 'All' && <Icon size={12} />}
                  {sp}
                </button>
              );
            })}
          </div>

          {/* Contractor Cards Grid */}
          {filtered.length === 0 ? (
            <div className="text-center p-16 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl">
              <Briefcase size={48} className="mx-auto text-[var(--t-border-hover)] mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">No Contractors Found</h3>
              <p className="text-[var(--t-text-muted)]">
                No contractors match your current filter. Try a different specialty or search term.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((contractor) => {
                const SpecialtyIcon = SPECIALTY_ICONS[contractor.specialty] || Wrench;
                return (
                  <div
                    key={contractor.id}
                    className="relative overflow-hidden bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-6 flex flex-col gap-4 hover:border-[var(--t-primary)]/30 transition-all group"
                  >
                    {/* Subtle glow */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--t-primary)]/5 rounded-full blur-3xl pointer-events-none group-hover:bg-[var(--t-primary)]/10 transition-all" />

                    {/* Top row: Avatar + Status */}
                    <div className="flex items-start justify-between gap-3 relative z-10">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-[var(--t-primary-dim)] flex items-center justify-center text-lg font-black text-[var(--t-primary)] uppercase">
                          {contractor.name.slice(0, 2)}
                        </div>
                        <div>
                          <p className="font-bold text-white leading-tight">{contractor.name}</p>
                          <p className="text-xs text-[var(--t-text-muted)] mt-0.5">{contractor.company}</p>
                        </div>
                      </div>
                      <span
                        className={`shrink-0 text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                          contractor.status === 'active'
                            ? 'bg-[var(--t-success-dim)] text-[var(--t-success)] border-[var(--t-success)]/20'
                            : 'bg-[var(--t-surface-hover)] text-[var(--t-text-muted)] border-[var(--t-border)]'
                        }`}
                      >
                        {contractor.status}
                      </span>
                    </div>

                    {/* Specialty + Rating */}
                    <div className="flex items-center justify-between relative z-10">
                      <span className="flex items-center gap-1.5 text-xs font-bold text-[var(--t-text-muted)] bg-[var(--t-bg)] px-2.5 py-1 rounded-lg border border-[var(--t-border)]">
                        <SpecialtyIcon size={12} className="text-[var(--t-primary)]" />
                        {contractor.specialty}
                      </span>
                      <StarRating rating={contractor.rating} />
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-[var(--t-border)] relative z-10" />

                    {/* Contact Info */}
                    <div className="space-y-2 relative z-10">
                      <a
                        href={`tel:${contractor.phone}`}
                        className="flex items-center gap-2 text-xs text-[var(--t-text-muted)] hover:text-[var(--t-primary)] transition-colors"
                      >
                        <Phone size={13} /> {contractor.phone}
                      </a>
                      <a
                        href={`mailto:${contractor.email}`}
                        className="flex items-center gap-2 text-xs text-[var(--t-text-muted)] hover:text-[var(--t-primary)] transition-colors truncate"
                      >
                        <Mail size={13} /> {contractor.email}
                      </a>
                      <div className="flex items-center gap-2 text-xs text-[var(--t-text-muted)]">
                        <MapPin size={13} /> {contractor.location}
                      </div>
                    </div>

                    {/* Footer Stats */}
                    <div className="flex items-center justify-between pt-2 border-t border-[var(--t-border)] relative z-10">
                      <div className="flex items-center gap-1.5 text-xs text-[var(--t-text-muted)]">
                        <CheckCircle size={12} className="text-[var(--t-success)]" />
                        <span className="font-semibold text-white">{contractor.jobsCompleted}</span> jobs done
                      </div>
                      <div className="text-[9px] font-mono text-[var(--t-text-muted)] opacity-60">
                        {contractor.licenseNo}
                      </div>
                    </div>

                    {/* Action row */}
                    <div className="flex gap-2 relative z-10">
                      <a
                        href={`tel:${contractor.phone}`}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[var(--t-bg)] border border-[var(--t-border)] text-xs font-semibold text-[var(--t-text)] hover:border-[var(--t-primary)] hover:text-[var(--t-primary)] transition-all"
                      >
                        <Phone size={13} /> Call
                      </a>
                      <button
                        onClick={() => toast.success(`Job assignment to ${contractor.name} — coming soon!`)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[var(--t-primary-dim)] border border-[var(--t-primary)]/20 text-xs font-semibold text-[var(--t-primary)] hover:bg-[var(--t-primary)] hover:text-white transition-all"
                      >
                        <Briefcase size={13} /> Assign Job
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
